import React, { useCallback, useEffect, useState } from 'react';
import { Avatar } from '@/components/console/Avatar';
import { useT, type LabelKey } from '@/i18n/labels';
import { useAuthStore } from '@/stores/authStore';
import { useAgentLoad } from '@/hooks/useAgentLoad';
import { useConsole } from '@/console/ConsoleContext';
import axiosClient from '@/api/axiosClient';
import API from '@/api/endpoints';
import type { AgentSessionDto, AgentProfile } from '@/types';

const POLL_MS = 15000;

/** Normalize the hub's lowercase availability into a dot class + label key. */
function availView(raw: string): { dot: string; key: LabelKey } {
  const a = (raw ?? '').toLowerCase();
  if (a === 'online' || a === 'available') return { dot: 'online', key: 'available' };
  if (a === 'busy') return { dot: 'busy', key: 'busy' };
  if (a === 'away') return { dot: 'away', key: 'away' };
  return { dot: 'offline', key: 'offlineStatus' };
}

interface RosterRow {
  agentId: string;
  availability: string;
  cap: number | null;
}

export const TeamPage: React.FC = () => {
  const { t } = useT();
  const user = useAuthStore((s) => s.user);
  const { hub } = useConsole();
  // Caller's own live load (the only cross-agent load the API exposes).
  const myLoad = useAgentLoad(user?.agentId, hub.status === 'connected');

  const [rows, setRows] = useState<RosterRow[]>([]);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axiosClient.get<AgentSessionDto[]>(API.AGENTS.ONLINE);
      const agents = res.data ?? [];
      // Resolve each agent's concurrency cap (best-effort, parallel).
      const withCaps = await Promise.all(
        agents.map(async (a) => {
          let cap: number | null = null;
          try {
            const p = await axiosClient.get<AgentProfile>(API.AGENTS.PROFILE(a.agentId));
            cap = p.data?.maxConcurrentConversations ?? null;
          } catch {
            cap = null;
          }
          return { agentId: a.agentId, availability: a.availability, cap };
        }),
      );
      setRows(withCaps);
      setLoadError(false);
    } catch {
      setLoadError(true);
    }
  }, []);

  useEffect(() => {
    load();
    const poll = window.setInterval(load, POLL_MS);
    return () => window.clearInterval(poll);
  }, [load]);

  const total = rows.length;
  const online = rows.filter((r) => availView(r.availability).dot !== 'offline').length;

  return (
    <div className="ac-page ac-fade-up" data-testid="team-page">
      <div className="ac-page-head">
        <h1 className="ac-page-title">{t('team_title')}</h1>
        <div className="ac-page-sub">{t('team_sub', { online, total })}</div>
      </div>

      {loadError && (
        <div role="alert" className="ac-card" style={{ padding: '12px 20px', marginBottom: 'var(--gap)', color: 'var(--warning)', fontWeight: 600 }}>
          {t('team_load_error')}
        </div>
      )}

      {!loadError && rows.length === 0 ? (
        <div className="ac-card" style={{ padding: '48px 40px' }}>
          <div className="ac-empty" style={{ height: 'auto' }}>
            <div className="ac-empty-title">{t('team_empty')}</div>
          </div>
        </div>
      ) : (
        <div className="ac-team-grid ac-stagger">
          {rows.map((a) => {
            const me = a.agentId === user?.agentId;
            const av = availView(a.availability);
            const cap = a.cap ?? 0;
            const load = me ? myLoad.load : null;
            const pct = me && cap > 0 && load != null ? Math.round((load / cap) * 100) : 0;
            const full = load != null && cap > 0 && load >= cap;
            const displayName = me ? user?.displayName || a.agentId : a.agentId;
            return (
              <div key={a.agentId} className={'ac-card ac-team-card' + (me ? ' me' : '')} data-testid={`team-card-${a.agentId}`}>
                {me && (
                  <span className="ac-badge takeover" style={{ position: 'absolute', top: 14, insetInlineEnd: 14 }}>
                    {t('team_you')}
                  </span>
                )}
                <div className="ac-team-top">
                  <Avatar name={displayName} seed={a.agentId} size={48} className="ac-team-av">
                    <span className={'ac-team-status ac-status-dot ' + av.dot} />
                  </Avatar>
                  <div>
                    <div className="ac-team-name" title={displayName}>{displayName}</div>
                    <div className="ac-team-role">{t('role_agent')}</div>
                  </div>
                </div>
                <div className="ac-team-metrics">
                  <div>
                    <div className="ac-team-metric-label">{t('team_load')}</div>
                    <div className="ac-team-metric-val">
                      {load != null ? load : <span className="na">{t('na_short')}</span>}
                      {cap > 0 && (
                        <span style={{ fontSize: 12, color: 'var(--text-faint)', fontWeight: 600 }}> / {cap}</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="ac-team-metric-label">{t('team_resolved')}</div>
                    <div className="ac-team-metric-val na">{t('na_short')}</div>
                  </div>
                  <div>
                    <div className="ac-team-metric-label">{t('team_aht')}</div>
                    <div className="ac-team-metric-val na">{t('na_short')}</div>
                  </div>
                  <div>
                    <div className="ac-team-metric-label">{t('team_capacity')}</div>
                    <div className="ac-team-metric-val" style={{ fontSize: 14 }}>{cap || t('na_short')}</div>
                  </div>
                </div>
                <div className="ac-capacity">
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-faint)', fontWeight: 600 }}>
                    <span>{t(av.key)}</span>
                    <span>{me && cap > 0 && load != null ? pct + '%' : ''}</span>
                  </div>
                  <div className="ac-capacity-bar">
                    <div className={'ac-capacity-fill' + (full ? ' full' : '')} style={{ width: pct + '%' }} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
